import { apiClient } from './ApiClient'
import type {
  ContextGap,
  CurrentGapAnalysisResult,
  StructuredBusinessContext,
} from '@/types/api'

/**
 * Backend domain keys (snake_case) → frontend domain keys (camelCase).
 * The source-of-truth endpoint returns the full object keyed by the
 * backend's internal domain taxonomy; the frontend schema uses camelCase.
 */
const SOT_KEY_MAP: Record<string, string> = {
  target_market: 'targetMarket',
  business_model: 'businessModel',
  go_to_market: 'goToMarket',
}

/**
 * Normalize a raw source-of-truth object from the backend so its top-level
 * keys match the frontend `StructuredBusinessContext` schema (camelCase).
 *
 * Handles three cases:
 * 1. Backend returns snake_case keys → remapped to camelCase
 * 2. Backend already returns camelCase → passed through
 * 3. Mixed input → each key handled individually
 */
function normalizeSourceOfTruth(
  raw: Record<string, unknown>
): StructuredBusinessContext {
  const normalized: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(raw)) {
    const camelKey = SOT_KEY_MAP[key] ?? key
    normalized[camelKey] = value
  }
  return normalized as StructuredBusinessContext
}

const SOT_DOMAINS = [
  'mission',
  'targetMarket',
  'businessModel',
  'product',
  'goToMarket',
  'competition',
  'team',
  'fundraising',
] as const
type SourceOfTruthDomain = (typeof SOT_DOMAINS)[number]

/** Query filters accepted by GET /v1/startups/{id}/context/gap-analysis/current. */
export interface GapListFilters {
  domain?: string
  priority?: 'critical' | 'high' | 'medium' | 'low'
  status?: 'open' | 'in_progress' | 'resolved' | 'ignored'
  page?: number
  limit?: number
}

const scopedContext = (startupId: string) =>
  `/v1/startups/${encodeURIComponent(startupId)}/context`

/**
 * Wraps /v1/startups/{startupId}/context — the canonical Phase 3+ surface.
 *
 * The Context Explorer v1 unscoped /v1/context/* methods that used to live
 * here moved to `services/legacy/LegacyContextService.ts` in commit 08, and
 * an eslint `no-restricted-imports` rule prevents new code from reaching for
 * them. Keep this class small: only the `/v1/startups/{id}/context/*` shape.
 */
export class ContextService {
  /**
   * GET /v1/startups/{id}/context/source-of-truth[?domain=...]
   *
   * Unwraps the `{ sourceOfTruth, domain? }` envelope and returns the
   * structured context. When `domain` is supplied the response contains
   * only that single section; callers should narrow before rendering.
   */
  async getSourceOfTruth(
    startupId: string,
    domain?: SourceOfTruthDomain
  ): Promise<StructuredBusinessContext> {
    const params = domain ? { domain } : undefined
    const res = await apiClient.get<{
      sourceOfTruth?: StructuredBusinessContext
      domain?: SourceOfTruthDomain | null
    }>(`${scopedContext(startupId)}/source-of-truth`, params)
    const raw = (res.sourceOfTruth ?? {}) as Record<string, unknown>
    return normalizeSourceOfTruth(raw)
  }

  /**
   * GET /v1/startups/{id}/context/gap-analysis/current
   *
   * Canonical "what gaps are open right now" query. Unwraps the
   * `{ gapAnalysis, filters, pagination }` envelope and returns the
   * latest persisted `CurrentGapAnalysisResult`. Prefer this over the
   * legacy `/context/gaps` endpoint, which returns a flat shape without
   * status counts.
   */
  async getGaps(
    startupId: string,
    filters?: GapListFilters
  ): Promise<CurrentGapAnalysisResult> {
    const params = filters ? gapFiltersToParams(filters) : undefined
    const res = await apiClient.get<{
      gapAnalysis?: CurrentGapAnalysisResult
    }>(`${scopedContext(startupId)}/gap-analysis/current`, params)
    const analysis = res.gapAnalysis ?? emptyGapAnalysis(startupId)
    return { ...analysis, gaps: (analysis.gaps ?? []).map(normalizeGap) }
  }

}

export const contextService = new ContextService()

// ─── Local helpers ─────────────────────────────────────────────────────

/**
 * The gap analyzer persists `gapId`, but openapi yaml (ContextGap.id, required)
 * and every consumer use `id`. Without this the UI has `undefined` for each
 * gap's identity, which breaks React keys, accordion open/close state and
 * per-gap upload targeting.
 *
 * Rename only — no field is invented, and an `id` sent by the backend wins.
 */
function normalizeGap(gap: ContextGap & { gapId?: string }): ContextGap {
  if (gap.id) return gap
  const { gapId, ...rest } = gap
  return { ...rest, id: gapId ?? '' } as ContextGap
}

function gapFiltersToParams(filters: GapListFilters): Record<string, string> {
  const rec: Record<string, string> = {}
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      rec[key] = String(value)
    }
  }
  return rec
}

function emptyGapAnalysis(startupId: string): CurrentGapAnalysisResult {
  return {
    uid: '',
    startupId,
    analyzedAt: null,
    gapsFound: 0,
    statusCounts: { open: 0, inProgress: 0, resolved: 0, ignored: 0 },
    gaps: [],
  }
}
